import * as THREE from 'three';
import type { BattleEvent, BattleState, Zone } from '@somnhold/sim';
import type { FogBank } from '../engine/outskirts.ts';
import type { Vfx } from '../engine/vfx.ts';

/**
 * The day's omen, seen (cosmetic only). Each omen dresses the battle in its own weather and light and
 * draws its own marks: the storm's lightning, the falling stars, the frost on the ground, the eyes in the
 * fog. Everything is procedural, instanced and animated in shaders, so it costs a handful of draw calls;
 * `low` quality draws fewer particles. The view builds one with `buildOmen` and feeds it ticks and frames.
 */

export interface OmenCtx {
  scene: THREE.Scene;
  /** Down in the crypt: no sky, no rain; the light comes from torches. */
  crypt: boolean;
  low: boolean;
  /**
   * A bastion wave (docs/PRESAGIOS.md): up to some 1800 figures, so nothing drawn per nightmare (RENDIMIENTO.md,
   * «Reglas para lo nuevo»); the sky, the light and the marks only.
   */
  crowd?: boolean;
  /** The board's sky lights (the crypt has no rim). */
  hemi: THREE.HemisphereLight | null;
  key: THREE.DirectionalLight | null;
  rim: THREE.DirectionalLight | null;
  /** The fog bank at the edge of the world (outdoors). */
  fogBank: FogBank | undefined;
  vfx: Vfx;
  flare(p: THREE.Vector3, color: string, size: number, life: number): void;
  shake(amount: number): void;
  /** Ground height at a world point. */
  ground(x: number, z: number): number;
  /** Where an entity (hero, nightmare) is drawn this frame, if it is. */
  pos(id: number): THREE.Vector3 | undefined;
  /** Sim (x, y) → world point on the ground, lifted by `h`. */
  at(x: number, y: number, h?: number): THREE.Vector3;
}

export interface OmenScene {
  group: THREE.Object3D;
  /** Every fresh sim tick, after its events. */
  tick?(s: BattleState): void;
  /** A sim event; true when the omen drew it (the view skips its own drawing). */
  event?(s: BattleState, e: BattleEvent): boolean;
  /** Draws a zone of its own (bolt, star); true when it did. */
  zone?(s: BattleState, z: Zone, p: THREE.Vector3): boolean;
  frame?(t: number, camera: THREE.Camera): void;
  dispose(): void;
}

export const TAU = Math.PI * 2;

/** A small deterministic hash (for looks only). */
export const hash = (n: number): number => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/** A cheap random stream for looks (never the sim's). */
export const looksRng = (seed: number): (() => number) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
    return s / 4294967296;
  };
};

export interface SkyTint {
  hemi?: [THREE.ColorRepresentation, THREE.ColorRepresentation, number];
  key?: [THREE.ColorRepresentation, number];
  rim?: [THREE.ColorRepresentation, number];
  background?: THREE.ColorRepresentation;
  fog?: THREE.ColorRepresentation;
  mist?: { low: string; mid: string; far: string; alpha: number };
}

/**
 * Re-lights the board for an omen (colors set, intensities multiplied); `restore` puts it back. `pulse`
 * scales the lights from there every frame (lightning, a heartbeat).
 */
export class SkyLook {
  private base: { hemi?: [THREE.Color, THREE.Color, number]; key?: [THREE.Color, number]; rim?: [THREE.Color, number]; bg?: THREE.Color; fog?: THREE.Color } = {};
  readonly lit = { hemi: 0, key: 0, rim: 0 };
  private bg: THREE.Color | null = null;
  private tmp = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    tint: SkyTint,
  ) {
    const { hemi, key, rim, scene } = ctx;
    if (hemi) {
      this.base.hemi = [hemi.color.clone(), hemi.groundColor.clone(), hemi.intensity];
      if (tint.hemi) {
        hemi.color.set(tint.hemi[0]);
        hemi.groundColor.set(tint.hemi[1]);
        hemi.intensity *= tint.hemi[2];
      }
      this.lit.hemi = hemi.intensity;
    }
    if (key) {
      this.base.key = [key.color.clone(), key.intensity];
      if (tint.key) {
        key.color.set(tint.key[0]);
        key.intensity *= tint.key[1];
      }
      this.lit.key = key.intensity;
    }
    if (rim) {
      this.base.rim = [rim.color.clone(), rim.intensity];
      if (tint.rim) {
        rim.color.set(tint.rim[0]);
        rim.intensity *= tint.rim[1];
      }
      this.lit.rim = rim.intensity;
    }
    if (scene.background instanceof THREE.Color) {
      this.base.bg = scene.background.clone();
      if (tint.background && !ctx.crypt) scene.background.set(tint.background);
      this.bg = scene.background.clone();
    }
    if (scene.fog instanceof THREE.Fog) {
      this.base.fog = scene.fog.color.clone();
      if (tint.fog && !ctx.crypt) scene.fog.color.set(tint.fog);
    }
    if (tint.mist) ctx.fogBank?.tint(tint.mist);
  }

  /** Scales the omen's lights this frame (1 = as set), and lerps the sky toward `flash` by `f`. */
  pulse(k: { hemi?: number; key?: number; rim?: number }, flash?: THREE.Color, f = 0): void {
    const { hemi, key, rim, scene } = this.ctx;
    if (hemi) hemi.intensity = this.lit.hemi * (k.hemi ?? 1);
    if (key) key.intensity = this.lit.key * (k.key ?? 1);
    if (rim) rim.intensity = this.lit.rim * (k.rim ?? 1);
    if (flash && this.bg && scene.background instanceof THREE.Color) scene.background.copy(this.tmp.copy(this.bg).lerp(flash, f));
  }

  restore(): void {
    const { hemi, key, rim, scene } = this.ctx;
    const b = this.base;
    if (hemi && b.hemi) {
      hemi.color.copy(b.hemi[0]);
      hemi.groundColor.copy(b.hemi[1]);
      hemi.intensity = b.hemi[2];
    }
    if (key && b.key) {
      key.color.copy(b.key[0]);
      key.intensity = b.key[1];
    }
    if (rim && b.rim) {
      rim.color.copy(b.rim[0]);
      rim.intensity = b.rim[1];
    }
    if (b.bg && scene.background instanceof THREE.Color) scene.background.copy(b.bg);
    if (b.fog && scene.fog instanceof THREE.Fog) scene.fog.color.copy(b.fog);
  }
}

const PRECIP_VERT = /* glsl */ `
attribute vec4 aSeed; // x, y (0..1 in the box), z, phase
uniform float uTime;
uniform vec3 uCenter;
uniform vec3 uBox;     // half width, height, half depth
uniform float uFall;   // world units a second (negative rises)
uniform vec2 uWind;
uniform float uSway;
uniform vec2 uSize;    // billboard width, height
uniform float uTwinkle;
varying vec2 vUv;
varying float vA;
void main() {
  vUv = uv;
  float t = uTime + aSeed.w * 37.0;
  // Each one falls (or rises) through the box and wraps, carried by the wind and swaying.
  float y = fract(aSeed.y - t * uFall / uBox.y);
  vec3 p = vec3((aSeed.x * 2.0 - 1.0) * uBox.x, y * uBox.y, (aSeed.z * 2.0 - 1.0) * uBox.z);
  p.xz += uWind * (1.0 - y) * uBox.y * 0.3;
  p.x += sin(t * 1.3 + aSeed.w * 6.28) * uSway;
  p.z += cos(t * 1.1 + aSeed.w * 9.0) * uSway;
  // The box follows the camera's look on the ground, each one wrapping around it.
  vec3 c = uCenter;
  p.x = c.x + mod(p.x - c.x + uBox.x, uBox.x * 2.0) - uBox.x;
  p.z = c.z + mod(p.z - c.z + uBox.z, uBox.z * 2.0) - uBox.z;
  p.y += c.y;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  mv.xy += position.xy * uSize;
  // Fade in at the top of the box and out at the bottom, so nothing pops.
  vA = smoothstep(0.0, 0.12, y) * smoothstep(1.0, 0.8, y);
  if (uTwinkle > 0.0) vA *= 0.55 + 0.45 * sin(t * uTwinkle + aSeed.w * 40.0);
  gl_Position = projectionMatrix * mv;
}`;

const PRECIP_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
uniform float uRound;
varying vec2 vUv;
varying float vA;
void main() {
  vec2 q = vUv * 2.0 - 1.0;
  // A round flake or mote; a streak fades along its length and across it.
  float a = uRound > 0.5 ? 1.0 - smoothstep(0.2, 1.0, length(q)) : (1.0 - abs(q.x)) * (1.0 - smoothstep(0.3, 1.0, abs(q.y))) * (0.4 + 0.6 * vUv.y);
  if (a <= 0.01) discard;
  gl_FragColor = vec4(uColor, a * vA * uOpacity);
  #include <colorspace_fragment>
}`;

export interface PrecipOpts {
  count: number;
  /** Half width and depth of the box, and its height (world units). */
  box: [number, number, number];
  fall: number;
  wind?: [number, number];
  sway?: number;
  size: [number, number];
  color: THREE.ColorRepresentation;
  opacity: number;
  round?: boolean;
  additive?: boolean;
  twinkle?: number;
  /** How high the box starts over the ground. */
  lift?: number;
}

/** Rain, snow, embers, motes: a box of billboards around where the camera looks, moved in the shader. */
export class Precip {
  readonly mesh: THREE.Mesh;
  private mat: THREE.ShaderMaterial;
  private lift: number;

  constructor(o: PrecipOpts, seed: number) {
    const rnd = looksRng(seed);
    const geo = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    geo.index = quad.index;
    geo.setAttribute('position', quad.getAttribute('position'));
    geo.setAttribute('uv', quad.getAttribute('uv'));
    const seeds = new Float32Array(o.count * 4);
    for (let i = 0; i < seeds.length; i++) seeds[i] = rnd();
    geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 4));
    geo.instanceCount = o.count;
    this.lift = o.lift ?? 0;
    this.mat = new THREE.ShaderMaterial({
      vertexShader: PRECIP_VERT,
      fragmentShader: PRECIP_FRAG,
      transparent: true,
      depthWrite: false,
      blending: o.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      uniforms: {
        uTime: { value: 0 },
        uCenter: { value: new THREE.Vector3() },
        uBox: { value: new THREE.Vector3(...o.box) },
        uFall: { value: o.fall },
        uWind: { value: new THREE.Vector2(...(o.wind ?? [0, 0])) },
        uSway: { value: o.sway ?? 0 },
        uSize: { value: new THREE.Vector2(...o.size) },
        uColor: { value: new THREE.Color(o.color) },
        uOpacity: { value: o.opacity },
        uRound: { value: o.round ? 1 : 0 },
        uTwinkle: { value: o.twinkle ?? 0 },
      },
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 11;
  }

  frame(t: number, center: THREE.Vector3): void {
    (this.mat.uniforms.uTime as { value: number }).value = t;
    (this.mat.uniforms.uCenter as { value: THREE.Vector3 }).value.set(center.x, center.y + this.lift, center.z);
  }

  set opacity(v: number) {
    (this.mat.uniforms.uOpacity as { value: number }).value = v;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}

const ray = new THREE.Raycaster();
const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const mid = new THREE.Vector2(0, 0);

/** Where the camera looks on the ground (the middle of the screen). */
export const lookPoint = (camera: THREE.Camera, out: THREE.Vector3): THREE.Vector3 => {
  ray.setFromCamera(mid, camera);
  if (!ray.ray.intersectPlane(groundPlane, out)) out.set(0, 0, 0);
  return out;
};

/** How high a nightmare's eyes sit (world units), roughly, by kind. */
export const eyeHeight = (kind: string): number =>
  kind === 'ram' || kind === 'slime' ? 0.55 : kind === 'gargoyle' || kind === 'wraith' ? 0.7 : kind === 'wolf' ? 0.32 : kind === 'warden' || kind === 'magmaLord' || kind === 'colossus' || kind === 'witch' || kind === 'shell' ? 1.3 : 0.45;

/** Camera right, flattened, for pairs of eyes that face the viewer. */
export const cameraRight = (camera: THREE.Camera, out: THREE.Vector3): THREE.Vector3 => out.setFromMatrixColumn(camera.matrixWorld, 0).setY(0).normalize();

/** Bosses and big ones draw bigger effects. */
export const bigKind = (kind: string): boolean => kind === 'warden' || kind === 'magmaLord' || kind === 'colossus' || kind === 'witch' || kind === 'shell' || kind === 'ram' || kind === 'dragon';
